import React, { useState, useCallback, useMemo } from 'react';
import BlankIcon from '~/components/icons/BlankIcon';
import TStrings from '../Reports.i18n';
import ModalTextRow from '~/components/ModalTextRow';
import Select from '~/components/Select';
import TextInput from '~/components/TextInput';
import { Report, ReportType, TemplateIndex } from '../Report.types';
import Loading from '~/components/Loading';

type TemplateEditorProps = {
    reportToEdit?: Report | null; // if report exists, the user can edit it, if omitted, the user can create a new one
    onCancel: () => void;
    onDelete: (aReport: Report) => void;
    onCreate?: (templateSelected: TemplateIndex, aReport?: Report) => void; // creates a new report
    onEdit: (aReport: Report) => void; // updates an existing report
    onGoBack?: () => void;
    isLoading: boolean;
};

const TemplateEditor = ({
    onCancel,
    onCreate,
    onEdit,
    onDelete,
    onGoBack,
    isLoading,
    reportToEdit,
}: TemplateEditorProps) => {
    const [type, setType] = useState<ReportType>(
        reportToEdit?.type ?? ReportType.Clients,
    );
    const [reportName, setReportName] = useState<string>(
        reportToEdit?.name || '',
    );
    const [error, setError] = useState<string>('');
    const isEditMode = useMemo(() => reportToEdit != null, [reportToEdit]);

    const onSubmitCallback = useCallback(() => {
        if (!reportName) {
            setError(TStrings.NO_REPORT_NAME_ERROR);
        } else {
            setError('');
            if (isEditMode) {
                if (reportToEdit) {
                    onEdit({
                        ...reportToEdit,
                        name: reportName,
                    });
                }
            } else if (onCreate) {
                onCreate(TemplateIndex.BLANK_TEMPLATE, {
                    name: reportName,
                    type,
                    settings: {},
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportName, isEditMode, reportToEdit, type]);

    return (
        <div className="flex grow items-center flex-col">
            <div className="text-base font-semibold leading-6 text-gray-900">
                {isEditMode
                    ? TStrings.EDIT_REPORT_TITLE
                    : TStrings.CREATE_REPORT_TITLE}
            </div>
            <div className="flex flex-col w-full mt-4">
                {!isEditMode && (
                    <>
                        <div className="panel-label">
                            {TStrings.REPORT_TEMPLATE_TITLE}
                        </div>
                        {onGoBack && (
                            <ModalTextRow
                                text={TStrings.CREATE_BLANK_TEMPLATE_TITLE}
                                onClick={onGoBack}
                                leftIcon={
                                    <div className="absolute text-primary-700">
                                        <BlankIcon />
                                    </div>
                                }
                                rightIcon={
                                    <div className="btn-light-pill">
                                        {TStrings.EDIT_TEMPLATE_BUTTON}
                                    </div>
                                }
                            />
                        )}
                        <div className="panel-label mt-4">
                            {TStrings.REPORT_ON_TITLE}
                        </div>
                        <Select<ReportType, string>
                            id="type"
                            onChange={setType}
                            value={type}
                            inputClassName="w-full"
                            options={{
                                [ReportType.Clients]: ReportType.Clients,
                                [ReportType.Forms]: ReportType.Forms,
                                [ReportType.Events]: ReportType.Events,
                                [ReportType.PackageGrants]: 'Packages',
                                [ReportType.Classes]: ReportType.Classes,
                                [ReportType.Payments]: ReportType.Payments,
                                [ReportType.GiftCertificates]: 'Gift Certificates',
                                [ReportType.Invoices]: ReportType.Invoices,
                            }}
                        />
                    </>
                )}
                <div className="panel-label mt-4">
                    {TStrings.REPORT_NAME_TITLE}
                </div>
                <TextInput
                    value={reportName}
                    onChange={setReportName}
                    id="report-name"
                    required={true}
                    inputClassName="w-full max-w-lg"
                    placeholder={TStrings.REPORT_PLACEHOLDER}
                    error={error}
                />
                <div className="flex flex-row items-center mt-6">
                    <button
                        disabled={isLoading}
                        type="button"
                        onClick={onSubmitCallback}
                        className="btn-pill flex"
                    >
                        {isLoading
                            ? TStrings.SAVING_REPORT_TEMPLATE_BUTTON
                            : isEditMode
                              ? TStrings.EDIT_REPORT_TEMPLATE_BUTTON
                              : TStrings.CREATE_REPORT_TEMPLATE_BUTTON}
                    </button>
                    <button
                        disabled={isLoading}
                        type="button"
                        onClick={onCancel}
                        className="btn-gray-pill ml-4"
                    >
                        {TStrings.CANCEL_BUTTON}
                    </button>
                    {isEditMode && reportToEdit && (
                        <div className="flex grow justify-end">
                            <button
                                disabled={isLoading}
                                type="button"
                                onClick={() => onDelete(reportToEdit)}
                                className="text-red-700 hover:text-red-800 px-4"
                            >
                                {TStrings.DELETE_BUTTON}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
export default TemplateEditor;
